import { IChain } from '../../utils/useChains';
import { TransactionType } from '../../types';
import { AssetDto, FiatAssetDto, NftAssetDto } from '../../types/assets';
import { DataGridSelectionState } from '../../components/DataGrid/shared/types';

/*
 * COUNTERPARTIES
 */
export interface CounterpartyBaseDto {
  name?: string;
}

export interface AddressCounterpartyDto extends CounterpartyBaseDto {
  address: string;
  account?: string;
  orgContact?: {
    id: string;
    name: string;
  };
  counterpartyId: string;
}

export interface AccountCounterpartyDto extends CounterpartyBaseDto {
  address: undefined;
  counterpartyId: undefined;
  account?: {
    address: string;
    chain: string;
    id: string;
    name: string;
    provider: string;
  };
}

export interface PlatformCounterpartyDto extends CounterpartyBaseDto {
  address: undefined;
  account: never;
  counterpartyId: string;
  orgContact: undefined;
}

export type CounterpartyDto =
  | AddressCounterpartyDto
  | AccountCounterpartyDto
  | PlatformCounterpartyDto;

export const isAddressOnlyCounterparty = (
  counterparty: CounterpartyDto,
): counterparty is AddressCounterpartyDto => {
  return !!counterparty.address;
};

export const isAccountCounterparty = (
  counterparty: CounterpartyDto,
): counterparty is AccountCounterpartyDto => {
  return typeof counterparty.account === 'object';
};

/*
 * incoming/outgoing
 */
export interface AssetMovementsDto {
  assets: AssetDto[];
  entities: CounterpartyDto[];
}

/*
 * TRANSACTIONS
 */
export interface RelatedNftCollectionDto {
  chain: string;
  contractAddress: string;
  id: string;
  name: string;
}
export interface RelatedNftTokenDto {
  amount: string;
  nftTokenId: string;
  name?: string;
}

export interface TxnBookAccountDto {
  id: string;
  name: string;
  code: string | null;
}
export interface TxnAccountingClass {
  id: string;
  name: string;
  isEnabled: true;
  platformId: string[];
  children: string[];
}

// TxnTabError is used to indicate why a transaction cannot be moved to a certain tab.
// Why fee separate? On the UI, we can display the same error message for both the txn
// and fee portions. But till fees are split from txn, we should keep being direct about
// which portion of txn is causing the error.
export enum TxnTabError {
  noPrice = 'no_price',
  noFeePrice = 'no_fee_price',
  unprocessed = 'unprocessed',
  hidden = 'hidden',
  spam = 'spam',
  noBookAccount = 'no_book_account',
  noFeeBookAccount = 'no_fee_book_account',
}

export enum TxnTabErrorMessages {
  no_price = 'Missing Price',
  no_fee_price = 'Missing Fee Price',
  unprocessed = 'Transaction is still processing. Values may change once processing is complete.',
  hidden = 'Excluded',
  spam = 'Spam',
  no_book_account = 'Missing GL Account',
  no_fee_book_account = 'Missing Fee GL Account',
}

export type TxnGroupsErrorCounts = {
  [key in TxnTabError]: number;
};

export interface TxnGroupsCount {
  total: number | null;
  readyForReview?: number | null;
}
export const BLANK_TXN_GROUP_COUNT: TxnGroupsCount = {
  total: null,
  readyForReview: null,
};

export interface BkV2TabCounts {
  forReview: number | null;
  reviewed: number | null;
  synced: number | null;
  all: number | null;
}

export interface CategorizedByDto {
  id: string;
  name: string;
}

export interface TransactionBaseDto {
  id: string;
  timestamp: string;
  txnHash?: string;
  label: TransactionType;
  isDeposit: boolean;
  account?: {
    address: string;
    chain: string;
    id: string;
    name: string;
    provider: string;
  };
  accountingClass?: TxnAccountingClass;
  contract?: TransactionContract;
  counterparty?: CounterpartyDto;
  amount: string;
  usdAmount?: string;
  usdGainLoss?: string;
  memo?: string;
  contractName?: string;
  chain?: string;
  tags?: string[];
  relatedNftCollections?: RelatedNftCollectionDto[];
  relatedNftTokens?: RelatedNftTokenDto[];
  bookAccount?: TxnBookAccountDto;
  categorizedBy?: CategorizedByDto;
  errors?: TxnTabError[];
  // ASSETS
  asset: AssetDto;
  fiat: FiatAssetDto;
}

export interface TransactionDto extends TransactionBaseDto {
  symbol: string;
}
export interface NftTransactionDto extends TransactionBaseDto {
  nftTokenId?: string;
  nftTokenName?: string;
  nftCollectionName: string;
}
export interface FeeTransactionDto extends TransactionBaseDto {
  symbol: string;
}

export interface TransactionContractBrand {
  company: string;
  id: number;
  logoUrl: string;
  url: string;
}

export interface TransactionContract {
  address: string;
  chain: string;
  id: string;
  name: string;
  brand?: TransactionContractBrand;
}

export const BookkeepingV2TransactionTabs = {
  FOR_REVIEW: 'forReview',
  REVIEWED: 'reviewed',
  SYNCED: 'synced',
  ALL: 'all',
} as const;
export type BookkeepingV2TransactionTabKeys =
  (typeof BookkeepingV2TransactionTabs)[keyof typeof BookkeepingV2TransactionTabs];

export const BookkeepingV2MoveToOptions = {
  FOR_REVIEW: 'forReview',
  REVIEWED: 'reviewed',
  SYNCED: 'synced',
  SPAM: 'spam',
  EXCLUDED: 'excluded',
} as const;
export type BookkeepingV2MoveToOptionKeys =
  (typeof BookkeepingV2MoveToOptions)[keyof typeof BookkeepingV2MoveToOptions];

// TXN GROUPS
export type TransactionGroupSubTxn = (TransactionDto | NftTransactionDto | FeeTransactionDto) & {
  price: {
    method: 'automatic' | 'manual';
    rate?: any;
    source: string;
  };
  symbol: string;
  outgoing: AssetMovementsDto;
  incoming: AssetMovementsDto;
  __metadata__?: {
    shouldGLAccountBeEditable: boolean;
    bookAccount?: TxnBookAccountDto | null;
  };
};
export interface TxnGroupHash {
  chain: string;
  timestamp: string;
  txnHash: string;
}

interface TransactionTagDto {
  id: string;
  name: string;
}

enum TxnGroupManualGroupingAction {
  Grouped = 'grouped',
  Ungrouped = 'ungrouped',
}

export interface TransactionGroupDto {
  id: string;
  timestamp: string;
  chain?: IChain;
  txnHashes?: TxnGroupHash[];
  label: TransactionType; // Internal "label id" (see also `displayLabel`)
  displayLabel: string; // Label displayed in the UI (see also `label`)
  outgoing?: AssetMovementsDto;
  incoming?: AssetMovementsDto;
  memo: string;
  transactions: TransactionGroupSubTxn[];
  accountingClass?: TxnAccountingClass;
  bookAccount?: TxnBookAccountDto;
  tags: TransactionTagDto[];
  tab: (typeof BookkeepingV2TransactionTabs)[keyof typeof BookkeepingV2TransactionTabs];
  errors?: TxnTabError[];
  manuallyGroupedBy?: string; // User ID
  recentManualGroupingAction?: TxnGroupManualGroupingAction;
  contracts: TransactionContract[];
  isDeposit: boolean;
  moveTo: Omit<
    (typeof BookkeepingV2TransactionTabs)[keyof typeof BookkeepingV2TransactionTabs],
    'all'
  >;

  // ASSETS
  fiat?: FiatAssetDto;
  feeFiat?: FiatAssetDto;
  gainLoss?: FiatAssetDto;
  __metadata__?: {
    uniqueTimestampCount: number;
    shouldGLAccountBeEditable: boolean;
    bookAccount: TxnBookAccountDto | null;
    groupCategorizedBy?: CategorizedByDto;
    groupClass: TxnAccountingClass | null;
    uniqueRelatedNftTokens: string[];
    uniqueRelatedNftCollections: string[];
    processedAssetsGroup: {
      incoming: Record<
        string,
        {
          assets: AssetDto[];
          nfts: NftAssetDto[];
          isMissingPrice: boolean;
        }
      >;
      outgoing: Record<
        string,
        {
          assets: AssetDto[];
          nfts: NftAssetDto[];
          isMissingPrice: boolean;
        }
      >;
    };
  };
}

export interface BulkUpdateResponseTransactionGroupDto {
  count: number; // number of valid transactions udpated

  // Async=false related (i.e. when `count > MAGIC_MAX_BULK_UPDATE_SIZE`)
  txnGroups: TransactionGroupDto[]; // empty array (`[]`) when async=true
  errorCounts: TxnGroupsErrorCounts; // e.g. { no_price: 5, no_book_account: 24 }, empty object (`{}`) when async=true

  // Async related response items
  async: boolean; // whether the update is occuring in the API thread vs Temporal Workflow (e.g. uses Temporal Workflow (async=true) when `count > MAGIC_MAX_BULK_UPDATE_SIZE`)
  workflowId: string; // empty string ('') if async=false
}

export interface GetTransactionGroupsDto {
  page?: number;
  limit?: number;
  tabSelection?: BookkeepingV2TransactionTabKeys;
  filtersQueryString?: string;
  sortByQueryString?: string;
}

export interface AddTxnGroupDto {
  groupAs: ManualGroupingTypeId;
  txnGroupIds: string[];
  ignoreWarnings?: boolean;
}

// Bookkeeping - manual grouping
export enum ManualGroupingTypeId {
  Bridge = 'bridge',
  Exchange = 'exchange',
  InternalTransfer = 'internal_transfer',
  Stake = 'stake',
  Unstake = 'unstake',
}
export enum ManualGroupingErrorCodes {
  // general
  NoInflows = 'noInflows',
  NoOutflows = 'noOutflows',
  // time
  NotWithinTimeRange = 'notWithinTimeRange',
  // same chain
  NotSameChainForInflows = 'notSameChainForInflows',
  NotSameChainForOutflows = 'notSameChainForOutflows',
  TxnNotInForReview = 'txnNotInForReview',
  // too far apart
  InflowsAndOutflowsTooFarApartThreePercent = 'inflowsAndOutflowsTooFarApartThreePercent',
  InflowsAndOutflowsTooFarApartOnePercent = 'inflowsAndOutflowsTooFarApartOnePercent',
  InflowsAndOutflowsNotEqualThreePercent = 'inflowsAndOutflowsNotEqualThreePercent',
  InflowsAndOutflowsNotEqualOnePercent = 'inflowsAndOutflowsNotEqualOnePercent',
  // same chain
  InflowsAndOutflowsSameChain = 'inflowsAndOutflowsSameChain',
  NotSameChain = 'notSameChain',
  // asset
  SameAsset = 'sameAsset',
  NotSameAsset = 'notSameAsset',
  // MISC
  NotEnoughTxns = 'notEnoughTxns',
  TooManyTxns = 'tooManyTxns',
  TxnNotFound = 'txtNotFound',
  NoTxnsFound = 'noTxnsFound',
  TxnNotInGroup = 'txnNotInGroup',
  GroupedTxnsMissing = 'groupedTxnsMissing', // Transactions within the same group are missing.
  NotOnChain = 'notOnChain',
  UnsupportedLabel = 'unsupportedLabel',
  // ^ This may change, but for now (no unbundle).
  FeeMissing = 'feeMissing', // User provided <txnId> but not <txnId>:fee
  TxnMissing = 'txnMissing', // User provided <txnId>:fee but not <txnId>
  NoChain = 'noChain',
  InternalError = 'internalError',
  InflowsAndOutflowsNotEqual = 'inflowsAndOutflowsNotEqual',
  MultipleChains = 'multipleChains',
}

export enum ManualGroupingErrorMessages {
  // general
  noInflows = 'At least 1 incoming',
  noOutflows = 'At least 1 outgoing',
  // time
  notWithinTimeRange = 'Transactions occurred within 1 hour',
  // same chain
  notSameChainForInflows = 'All incoming are on the same chain',
  notSameChainForOutflows = 'All outgoing are on the same chain',
  txnNotInForReview = 'All transactions are For Review',
  // too far apart
  inflowsAndOutflowsTooFarApartThreePercent = 'Value of incoming and outgoing match (<3%)',
  inflowsAndOutflowsTooFarApartOnePercent = 'Value of incoming and outgoing match (<1%)',
  inflowsAndOutflowsNotEqualThreePercent = 'Value of incoming and outgoing match (<3%)',
  inflowsAndOutflowsNotEqualOnePercent = 'Value of incoming and outgoing match (<1%)',
  // same chain
  inflowsAndOutflowsSameChain = 'Incoming and outgoing are on different chains',
  notSameChain = 'All transactions are on the same chain',
  // asset
  sameAsset = 'Incoming and outgoing tokens are different',
  notSameAsset = 'All transactions are the same token',
  // MISC
  notEnoughTxns = 'There should be enough transactions',
  noChain = 'At least 1 transaction has a chain',
  inflowsAndOutflowsNotEqual = 'Total value of incoming transactions equals total value of outgoing transactions',
  multipleChains = 'All transactions are on the same chain',
  notOnChain = 'Transaction is on-chain',
  unsupportedLabel = 'Valid label for type of transaction',
}

export const ManualGroupingErrorMessageSubtitle: { [code in ManualGroupingErrorCodes]?: string } = {
  [ManualGroupingErrorCodes.NotOnChain]:
    'Transactions from CSV imports or from centralized exchanges are not supported',
};

export const TxnTypesThatSupportUngrouping = [
  TransactionType.InternalTransfer,
  TransactionType.Exchange,
  TransactionType.Bridge,
  TransactionType.Stake,
  TransactionType.Unstake,
  TransactionType.Withdrawal,
  TransactionType.Deposit,
] as const;
export type TxnTypesThatSupportUngroupingType = (typeof TxnTypesThatSupportUngrouping)[number];

export interface UngroupTxnGroupDto {
  id: string;
}

export type UpdateTxnGroupLabelType =
  | TransactionType.InternalLoanRepayment
  | TransactionType.InternalLoan
  | TransactionType.Deposit
  | TransactionType.Withdrawal;

export interface UpdateTxnGroupDto {
  id: string;
  accountingClassId?: string;
  bookAccountId?: string;
  memo?: string;
  moveTo?: TransactionGroupDto['moveTo'];
  prices?: {
    method: 'automatic' | 'manual';
    nftId?: string;
    tokenId?: string;
    rate?: string;
  }[];
  tags?: string[];
  label?: UpdateTxnGroupLabelType;
}

export interface UpdateTxnGroupsDto {
  params?: GetTransactionGroupsDto;
  selectionState?: DataGridSelectionState;
  updates: {
    memo?: string;
    bookAccountId?: string;
    accountingClassId?: string;
    tagIds?: string[];
    moveTo?: string;
    hidden?: boolean;
  };
}

export interface UpdateTxnGroupSubTxnDto {
  txnGroupId: string;
  txnId: string;
  memo?: string;
  accountingClassId?: string;
  bookAccountId?: string;
}

export enum TransactionsUpdateStatus {
  Running = 'Running',
  Stopped = 'Stopped',
  Completed = 'Completed',
}

export interface UpdateTxnGroupsByQueryDto {
  status: TransactionsUpdateStatus;
  updatedTxns: number;
  totalTxns: number;
}

export enum BookkeepingV2ViewFilterType {
  HAS_RULE_APPLIED = 'hasRuleApplied',
  IS_READY_TO_REVIEW = 'readyToReview',
  IS_SPAM = 'isSpam',
  IS_EXCLUDED = 'isExcluded',
}

export enum BookkeepingV2WarningFilters {
  ERRORS = 'errors',
}

export const isTransactionGroup = (
  record: TransactionGroupDto | TransactionGroupSubTxn,
): record is TransactionGroupDto => {
  return 'transactions' in record;
};

export const isUpdateLabelSupported = (
  label: TransactionType,
): label is UpdateTxnGroupLabelType => {
  return [
    TransactionType.InternalLoanRepayment,
    TransactionType.InternalLoan,
    TransactionType.Deposit,
    TransactionType.Withdrawal,
  ].includes(label);
};
